import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { projectState, toastStateSelector } from 'state';
import { useFormik } from 'formik';
import { ToastSeverity } from 'state';
import Editor from 'react-simple-wysiwyg';

import {
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { mutation } from 'operations/mutation';
import { GeneralIds, ModelTypes, RoutePaths } from 'types/types';
import { Box } from '@mui/system';
import { getRoutePath } from 'utils';
import { getProjectInitialValues, getProjectValidationSchema } from 'forms';
import { getAvailablePatientFields } from 'api';
import { query } from 'operations';
import { Project } from 'api/models';
import { SaveButton } from 'components/SaveButton';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import useRequest from 'hooks/useRequest';

export default function ProjectEditor() {
  const { projectId } = useParams<GeneralIds>();
  const setToast = useSetRecoilState(toastStateSelector);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setProjectStore = useSetRecoilState(projectState);
  const project = useRecoilValue(projectState).selectedProject;

  const { data: availableFields } = useRequest<string[]>(
    getAvailablePatientFields,
    {
      dependencies: [projectId],
    },
  );

  const formik = useFormik({
    initialValues: getProjectInitialValues(project),
    enableReinitialize: true,
    validationSchema: getProjectValidationSchema(t),
    onSubmit: async values => {
      const {
        name,
        shortName,
        description,
        automaticProjectId,
        editableProjectId,
        samplesEnabled,
        queryModuleEnabled,
        requiredPatientFields,
        longDescription,
        repeatingFieldsEnabled,
      } = values;

      try {
        const { data: savedProject } = await mutation<Project>({
          type: ModelTypes.project,
          body: {
            name,
            shortName,
            description,
            longDescription,
            id: projectId ?? '',
            configuration: {
              automaticProjectId,
              editableProjectId,
              samplesEnabled,
              queryModuleEnabled,
              repeatingFieldsEnabled,
            },
            supportedPatientFields: requiredPatientFields,
            requiredPatientFields,
          },
          ids: {
            id: projectId,
          },
        });
        const { data: projects } = await query<Project[]>({
          type: ModelTypes.project,
        });
        setProjectStore({
          projects: projects,
          selectedProject: savedProject,
        });
        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
        navigate(getRoutePath(RoutePaths.projectDashboard, { projectId }));
      } catch (e) {
        console.error(e);
        setToast({
          isOpen: true,
          message: t('projectCreation.errors.creationFailed'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Paper
      sx={{
        padding: theme =>
          `${theme.spacing(0)} ${theme.spacing(2)} ${theme.spacing(
            1,
          )} ${theme.spacing(2)}`,
        mt: theme => theme.spacing(3),
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              inputProps={{ 'data-testid': 'project-creation-name' }}
              label={t('projectCreation.name') + ' *'}
              type="text"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              inputProps={{ 'data-testid': 'project-creation-shortName' }}
              label={t('projectCreation.shortName') + ' *'}
              type="text"
              name="shortName"
              value={formik.values.shortName}
              onChange={formik.handleChange}
              error={
                formik.touched.shortName && Boolean(formik.errors.shortName)
              }
              helperText={formik.touched.shortName && formik.errors.shortName}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              inputProps={{ 'data-testid': 'project-creation-description' }}
              label={t('projectCreation.description')}
              type="text"
              multiline={true}
              rows={4}
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <ClearableSelect
              sx={{ mt: 2 }}
              fullWidth
              label={t('projectCreation.requiredPatientFields')}
              name="requiredPatientFields"
              value={formik.values.requiredPatientFields}
              onChange={formik.handleChange}
              onClear={() => {
                formik.setFieldValue('requiredPatientFields', []);
              }}
              isClearable={true}
              error={
                formik.touched.requiredPatientFields &&
                Boolean(formik.errors.requiredPatientFields)
              }
              helperText={
                formik.touched.requiredPatientFields &&
                formik.errors.requiredPatientFields
              }
              multiple={true}
              renderValue={() => {
                return formik.values.requiredPatientFields?.map(value => (
                  <Chip
                    key={`selected-patient-field:${value}-value-chip`}
                    sx={{ mr: 1 }}
                    label={
                      t(`projectCreation.availablePatientFields.${value}`) ??
                      value
                    }
                    color={'primary'}
                  />
                ));
              }}
            >
              {availableFields?.map(field => (
                <MenuItem
                  key={`project-editor-available-field-${field}`}
                  value={field}
                  sx={{ zIndex: 5 }}
                >
                  <Checkbox
                    checked={
                      Array.isArray(formik.values.requiredPatientFields) &&
                      formik.values.requiredPatientFields.includes(field)
                    }
                  />
                  {t(`projectCreation.availablePatientFields.${field}`) ??
                    field}
                </MenuItem>
              ))}
            </ClearableSelect>

            <Grid xs={6} md={12} item mt={2}>
              <FormControlLabel
                control={
                  <Checkbox checked={formik.values.automaticProjectId} />
                }
                label={
                  <Typography variant="body2">
                    {t('projectCreation.automaticProjectId')}
                  </Typography>
                }
                name="automaticProjectId"
                onChange={formik.handleChange}
              />
              <FormControlLabel
                control={<Checkbox checked={formik.values.editableProjectId} />}
                label={
                  <Typography variant="body2">
                    {t('projectCreation.editableProjectId')}
                  </Typography>
                }
                name="editableProjectId"
                onChange={formik.handleChange}
              />
              <FormControlLabel
                control={<Checkbox checked={formik.values.samplesEnabled} />}
                label={
                  <Typography variant="body2">
                    {t('projectCreation.samplesEnabled')}
                  </Typography>
                }
                name="samplesEnabled"
                onChange={formik.handleChange}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={formik.values.queryModuleEnabled} />
                }
                label={
                  <Typography variant="body2">
                    {t('projectCreation.queryModuleEnabled')}
                  </Typography>
                }
                name="queryModuleEnabled"
                onChange={formik.handleChange}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={formik.values.repeatingFieldsEnabled} />
                }
                label={
                  <Typography variant="body2">
                    {t('projectCreation.repeatingFieldsEnabled')}
                  </Typography>
                }
                name="repeatingFieldsEnabled"
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
          <Grid xs={12} md={12} item>
            <FormControl fullWidth sx={{ mt: 5, mb: 2 }}>
              <InputLabel
                sx={{
                  position: 'absolute',
                  zIndex: 99990,
                  top: -50,
                  left: -10,
                }}
              >
                {t('projectCreation.longDescription')}
              </InputLabel>
              <Editor
                value={formik.values.longDescription}
                onChange={formik.handleChange}
                name="longDescription"
              />
            </FormControl>
          </Grid>
        </Grid>
        <Box display="flex" justifyContent="end">
          <SaveButton isLoading={formik.isSubmitting} />
        </Box>
      </form>
    </Paper>
  );
}
